import {
  LogEntry,
  LogFilters,
  LogLevel,
  LogEventType,
  SortField,
} from '../types'

export interface ESHit {
  _id: string
  _index: string
  _source: Record<string, unknown>
}

export interface ESSearchResponse {
  hits: {
    total: { value: number; relation?: string } | number
    hits: ESHit[]
  }
}

export const SERVICE_EVENT_TYPE_MAP: Record<string, LogEventType> = {
  accesscontrol: 'badge_event',
  alarms: 'alarm',
  analytics: 'system',
  apigateway: 'system',
  authentication: 'authentication',
  cartography: 'configuration_change',
  connectors: 'device_status',
  flow: 'configuration_change',
  geolocation: 'device_status',
  license: 'system',
  logproxy: 'system',
  missions: 'user_action',
  navigation: 'user_action',
  notifications: 'system',
  resources: 'configuration_change',
  simulator: 'system',
  sip: 'device_status',
  tasks: 'user_action',
  units: 'device_status',
  variablemessagesigns: 'device_status',
  videomanager: 'video_extraction',
  voip: 'device_status',
}

const EVENT_TYPE_TO_SERVICES: Record<LogEventType, string[]> = (() => {
  const acc: Record<string, string[]> = {}
  for (const [svc, ev] of Object.entries(SERVICE_EVENT_TYPE_MAP)) {
    if (acc[ev] === undefined) acc[ev] = []
    acc[ev].push(svc)
  }
  return acc as Record<LogEventType, string[]>
})()

export function mapLevel(esLevel: unknown): LogLevel {
  const raw = String(esLevel ?? 'Information').toLowerCase()
  if (raw.startsWith('err') || raw.startsWith('fat')) return 'ERROR'
  if (raw.startsWith('warn')) return 'WARN'
  if (raw.startsWith('debug') || raw.startsWith('verb')) return 'DEBUG'
  return 'INFO'
}

const LEVEL_SYNONYMS: Record<Exclude<LogFilters['level'], 'ALL'>, string[]> = {
  INFO: ['Information', 'INFO', 'Info'],
  WARN: ['Warning', 'WARN', 'Warn'],
  ERROR: ['Error', 'Fatal', 'ERROR'],
  DEBUG: ['Debug', 'Verbose', 'DEBUG'],
}

export function extractService(indexName: string): string {
  const stripped = indexName.replace(/^constellation-/, '')
  const match = stripped.match(/^([a-z]+)/)
  return match !== null ? match[1] : stripped
}

function getStringField(
  fields: Record<string, unknown>,
  key: string,
): string | null {
  const v = fields[key]
  return typeof v === 'string' ? v : null
}

export function transformHit(hit: ESHit): LogEntry {
  const src = hit._source
  const service = extractService(hit._index)
  const rawFields = src.fields
  const fields: Record<string, unknown> =
    rawFields !== null && typeof rawFields === 'object'
      ? (rawFields as Record<string, unknown>)
      : {}

  const ts = src['@timestamp']
  const description = getStringField(src, 'message')

  return {
    id: hit._id,
    timestamp: typeof ts === 'string' ? ts : new Date().toISOString(),
    type: SERVICE_EVENT_TYPE_MAP[service] ?? 'system',
    level: mapLevel(src.level),
    subsystem: service.toUpperCase(),
    user: getStringField(fields, 'Username'),
    description: description ?? '(no message)',
    metadata: {
      requestId: getStringField(fields, 'RequestId') ?? undefined,
      traceId: getStringField(fields, 'TraceId') ?? undefined,
      userId: getStringField(fields, 'PassportId') ?? undefined,
      clientIp: undefined,
      payload: buildPayload(src, fields),
    },
  }
}

export function buildPayload(
  src: Record<string, unknown>,
  fields: Record<string, unknown>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  const interesting = [
    'SourceContext',
    'EntityId',
    'EntityName',
    'ActionName',
    'CommandType',
    'MachineName',
    'IsSuccess',
    'ConnectionId',
    'HubMethod',
  ]
  for (const key of interesting) {
    if (fields[key] !== undefined && fields[key] !== null) {
      payload[key] = fields[key]
    }
  }
  if (src.messageTemplate !== undefined && src.messageTemplate !== null) {
    payload.messageTemplate = src.messageTemplate
  }
  if (src.exceptions !== undefined && src.exceptions !== null) {
    payload.exceptions = src.exceptions
  }
  return Object.keys(payload).length > 0 ? payload : { source: 'elasticsearch' }
}

const PRESET_TO_GTE: Record<string, string> = {
  last1h: 'now-1h',
  last24h: 'now-24h',
  last7d: 'now-7d',
  last30d: 'now-30d',
}

interface EsRange {
  gte?: string
  lte?: string
}

type EsClause = Record<string, unknown>

interface EsSortOptions {
  order: 'asc' | 'desc'
  missing?: '_first' | '_last'
  unmapped_type?: string
}

interface EsScriptSort {
  type: 'string'
  order: 'asc' | 'desc'
  script: {
    lang: 'painless'
    source: string
    params: Record<string, string>
  }
}

export type EsSortClause = Record<
  string,
  'asc' | 'desc' | EsSortOptions | EsScriptSort
>

export interface EsQueryBody {
  from: number
  size: number
  track_total_hits: true
  _source: { includes: string[] }
  sort: EsSortClause[]
  query: {
    bool: {
      must: EsClause[]
      filter: EsClause[]
    }
  }
}

const SOURCE_INCLUDES = [
  '@timestamp',
  'level',
  'message',
  'messageTemplate',
  'exceptions',
  'fields.SourceContext',
  'fields.ActionName',
  'fields.Username',
  'fields.RequestId',
  'fields.TraceId',
  'fields.PassportId',
  'fields.EntityId',
  'fields.EntityName',
  'fields.CommandType',
  'fields.MachineName',
  'fields.IsSuccess',
  'fields.ConnectionId',
  'fields.HubMethod',
]

export function escapeEsWildcard(value: string): string {
  return value.replace(/[\\*?]/g, '\\$&')
}

export const ES_MAX_RESULT_WINDOW = 10000

export function buildEsQuery(filters: LogFilters): EsQueryBody {
  const must: EsClause[] = []
  const filter: EsClause[] = []

  if (filters.datePreset === 'custom') {
    const range: EsRange = {}
    if (filters.dateFrom !== null && filters.dateFrom.length > 0) {
      range.gte = filters.dateFrom
    }
    if (filters.dateTo !== null && filters.dateTo.length > 0) {
      range.lte = filters.dateTo
    }
    if (range.gte !== undefined || range.lte !== undefined) {
      filter.push({ range: { '@timestamp': range } })
    }
  } else {
    const gte = PRESET_TO_GTE[filters.datePreset]
    if (gte !== undefined) {
      filter.push({ range: { '@timestamp': { gte, lte: 'now' } } })
    }
  }

  if (filters.subsystems.length > 0) {
    filter.push({
      bool: {
        should: filters.subsystems.map(subsystem => ({
          wildcard: {
            _index: `constellation-${subsystem.toLowerCase()}-*`,
          },
        })),
        minimum_should_match: 1,
      },
    })
  }

  if (filters.level !== 'ALL') {
    filter.push({ terms: { 'level.keyword': LEVEL_SYNONYMS[filters.level] } })
  }

  if (filters.eventTypes.length > 0) {
    const services = filters.eventTypes.flatMap(
      eventType => EVENT_TYPE_TO_SERVICES[eventType] ?? [],
    )
    if (services.length > 0) {
      filter.push({
        bool: {
          should: services.map(svc => ({
            wildcard: { _index: `constellation-${svc}-*` },
          })),
          minimum_should_match: 1,
        },
      })
    }
  }

  if (filters.users.length > 0) {
    filter.push({
      bool: {
        should: filters.users.flatMap(user => [
          {
            wildcard: {
              'fields.Username.keyword': {
                value: `*${escapeEsWildcard(user)}*`,
              },
            },
          },
          {
            wildcard: {
              'fields.Username': {
                value: `*${escapeEsWildcard(user.toLowerCase())}*`,
              },
            },
          },
        ]),
        minimum_should_match: 1,
      },
    })
  }

  if (filters.states.length > 0) {
    filter.push({ terms: { 'fields.CommandType.keyword': filters.states } })
  }

  if (filters.traceId.length > 0) {
    filter.push({
      bool: {
        should: [
          { term: { 'fields.TraceId.keyword': filters.traceId } },
          { match_phrase: { 'fields.TraceId': filters.traceId } },
        ],
        minimum_should_match: 1,
      },
    })
  }

  if (filters.requestId.length > 0) {
    filter.push({
      bool: {
        should: [
          { term: { 'fields.RequestId.keyword': filters.requestId } },
          { match_phrase: { 'fields.RequestId': filters.requestId } },
        ],
        minimum_should_match: 1,
      },
    })
  }

  if (filters.search.length > 0) {
    must.push({
      multi_match: {
        query: filters.search,
        type: 'best_fields',
        fields: [
          'message',
          'fields.RequestId',
          'fields.TraceId',
          'fields.Username',
        ],
      },
    })
  }

  const order: 'asc' | 'desc' = filters.sortOrder === 'oldest' ? 'asc' : 'desc'
  const sort = buildSortClauses(filters.sortField, order)

  // Clamp from so from + size never exceeds the result window — protects
  // against out-of-range pages (e.g. a stale ?page=500 in the URL).
  const maxFrom = Math.max(0, ES_MAX_RESULT_WINDOW - filters.limit)
  const from = Math.min(
    Math.max(0, (filters.page - 1) * filters.limit),
    maxFrom,
  )

  return {
    from,
    size: filters.limit,
    track_total_hits: true,
    _source: { includes: SOURCE_INCLUDES },
    sort,
    query: { bool: { must, filter } },
  }
}

const SORT_FIELD_TO_ES: Record<
  Exclude<SortField, 'timestamp' | 'source' | 'message'>,
  string
> = {
  level: 'level.keyword',
  machine: 'fields.MachineName.keyword',
  connection: 'fields.ConnectionId.keyword',
  state: 'fields.CommandType.keyword',
  user: 'fields.Username.keyword',
}

// The Message column displays formatLogMessage(entry) — a client-side rewrite
// of the raw message — so sorting the raw field would order rows by text the
// user never sees. This painless script mirrors the matchers that change the
// leading words (matchGateway strips the "requestId: …, message: " prefix;
// matchCommandResult collapses 'Processed: "Cmd" was UNSUCCESSFULL: Bool on
// Entity "Ent"' to "Cmd Ent succeeded|failed"; matchKafka rewrites the
// consumer lifecycle lines "Assigned partitions:/Revoking assignment:/
// Subscribed to:") and lowercases the key so the order is case-insensitive.
// Keep it in sync with formatLogMessage.ts when matchers that alter the start
// of the message are added or changed.
// `params.missing` handles messages longer than the mapping's ignore_above
// (256): they have no keyword doc-value to sort on, and the sentinel sends
// them to the end for the requested direction instead of clumping first.
const MESSAGE_SORT_SCRIPT = `
if (doc['message.keyword'].size() == 0) { return params.missing; }
String m = doc['message.keyword'].value;
if (m.startsWith('requestId: ')) {
  int i = m.indexOf(', message: ');
  if (i >= 0) { m = m.substring(i + 11); }
} else if (m.startsWith('Processed: "')) {
  int q = m.indexOf('"', 12);
  if (q >= 0) {
    int e1 = m.indexOf('on Entity "');
    if (e1 >= 0) {
      int e2 = m.indexOf('"', e1 + 11);
      if (e2 >= 0) {
        String cmd = m.substring(12, q);
        String ent = m.substring(e1 + 11, e2);
        boolean ok = m.contains('UNSUCCESSFULL: True');
        m = cmd + ' ' + ent + (ok ? ' succeeded' : ' failed');
      }
    }
  }
} else if (m.startsWith('Assigned partitions: [') && m.endsWith(']]]')) {
  int t = m.indexOf(' [[', 22);
  if (t >= 0 && m.indexOf(' ', 22) == t) {
    int e = m.indexOf(']]', t + 3);
    if (e >= 0) {
      m = 'Assigned partition ' + m.substring(t + 3, e) + ' to ' + m.substring(22, t);
    }
  }
} else if (m.startsWith('Revoking assignment: [')) {
  int t = m.indexOf(' [[', 22);
  if (t >= 0 && m.indexOf(' ', 22) == t) {
    int e = m.indexOf(']]', t + 3);
    if (e >= 0) {
      m = 'Revoked partition ' + m.substring(t + 3, e) + ' from ' + m.substring(22, t);
    }
  }
} else if (m.startsWith('Subscribed to: [') && m.endsWith(']')) {
  String topic = m.substring(16, m.length() - 1);
  if (topic.length() > 0 && topic.indexOf(' ') < 0 && topic.indexOf('[') < 0) {
    m = 'Subscribed to ' + topic;
  }
}
return m.toLowerCase();
`.trim()

// Elasticsearch stores @timestamp at millisecond precision while the services
// log with sub-millisecond bursts, so many documents tie on the sort key. Tie
// order between separate requests is arbitrary, which breaks from/size
// pagination: page N+1 can re-shuffle rows relative to page N (looks like
// only the current page was sorted). _doc as the final key makes the global
// order deterministic so consecutive page requests slice one stable sequence.
const TIE_BREAKER: EsSortClause = { _doc: 'asc' }

export function buildSortClauses(
  sortField: SortField,
  order: 'asc' | 'desc',
): EsSortClause[] {
  if (sortField === 'timestamp') return [{ '@timestamp': order }, TIE_BREAKER]

  if (sortField === 'source') {
    return [{ _index: order }, { '@timestamp': 'desc' }, TIE_BREAKER]
  }

  const primary: EsSortClause =
    sortField === 'message'
      ? {
          _script: {
            type: 'string',
            order,
            script: {
              lang: 'painless',
              source: MESSAGE_SORT_SCRIPT,
              params: { missing: order === 'asc' ? '￿' : '' },
            },
          },
        }
      : {
          [SORT_FIELD_TO_ES[sortField]]: {
            order,
            missing: '_last',
            unmapped_type: 'keyword',
          },
        }

  return [primary, { '@timestamp': 'desc' }, TIE_BREAKER]
}

export function totalFromResponse(res: ESSearchResponse): number {
  const t = res.hits.total
  if (typeof t === 'number') return t
  return t.value
}
